import { useCallback, useEffect, useState } from "react";

interface UseInlineEditorOptions<T> {
  validate?: (draft: T) => string | undefined;
}

export function useInlineEditor<T>(
  value: T,
  onSave: (draft: T) => Promise<void>,
  options?: UseInlineEditorOptions<T>,
) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [value, isEditing]);

  const startEdit = useCallback(() => {
    setDraft(value);
    setError(undefined);
    setIsEditing(true);
  }, [value]);

  const cancelEdit = useCallback(() => {
    setDraft(value);
    setError(undefined);
    setIsEditing(false);
  }, [value]);

  const save = useCallback(async () => {
    if (options?.validate) {
      const validationError = options.validate(draft);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setSaving(true);
    try {
      await onSave(draft);
      setIsEditing(false);
      setError(undefined);
    } catch {
      // Parent handles error feedback
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, options]);

  return {
    isEditing,
    draft,
    setDraft,
    saving,
    error,
    setError,
    startEdit,
    cancelEdit,
    save,
  };
}
